import {get, merge} from 'lodash';
import {ExperianStateBlacklist} from '@deliveriesondemand/dod-data';
import {Fragment, useState} from 'react';
import injectSheet from 'react-jss';
import {abbreviations} from '@deliveriesondemand/dod-states';
import Loader from 'react-loader-spinner';
import PropTypes from 'prop-types';

import {Control, ControlGroup} from '-/components/fields/Control';
import {getExperianDescription} from '-/api/api';
import Button from '-/components/general/Button';
import Select from '-/components/fields/Select';
import TextInput from '-/components/fields/TextInput';

const errorMsg = {
    color: 'red',
    border: '2px solid red',
    padding: 10,
    backgroundColor: 'white'
};

const styles = () => ({
    blacklistedMsg: {
        ...errorMsg,
        margin: '20px 0'
    },
    findVehicleButton: {
        marginTop: 20
    },
    lookupOptions: {
        '& li': {
            margin: '10px 0 0 30px',
            '& a': {
                textDecoration: 'underline',
                fontWeight: 'bold',
                cursor: 'pointer'
            }
        }
    }
});

const License = props => {
    const [failure, setFailure] = useState(false);
    const [failureCount, setFailureCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const {
        addressState,
        buyerName,
        classes = {},
        contactPhone,
        handleChange,
        handleTypeChange,
        theme = {},
        vehicleInfo: {
            licensePlate,
            licenseState
        } = {}
    } = props;
    const primaryColor = get(theme, 'colors.primary');
    const blacklisted = ExperianStateBlacklist.includes(licenseState);
    const noData = !licenseState || !licensePlate;
    const disableLicenseButton = noData || failureCount > 2 || blacklisted;
    const addressStateObj = {[addressState]: abbreviations[addressState]};
    const weighted = merge(addressStateObj, abbreviations);

    const handleFindVehicleClick = async() => {
        setLoading(true);
        const hasLicenseInfo = buyerName && licensePlate && licenseState;
        const shouldLookup = hasLicenseInfo && !ExperianStateBlacklist.includes(licenseState);
        let newFailure = false;

        if (shouldLookup) {
            try {
                const opts = {
                    name: buyerName,
                    licensePlate,
                    licenseState
                };

                const vehicleInfo = await getExperianDescription(opts);
                const {lienholder = {}, vin} = vehicleInfo;
                const {orgName = ''} = lienholder;

                handleChange({
                    ...vin && {vin},
                    ...orgName && {'lienholder.company.name': orgName}
                });
            } catch (ex) {
                newFailure = true;
            }
        }

        setFailure(newFailure);
        setLoading(false);

        if (newFailure) {
            setFailureCount(failureCount + 1);
        }
    };

    const handleLicensePlateChange = value => {
        handleChange({'licensePlate.number': value});
    };

    const handleLicenseStateChange = value => {
        handleChange({'licensePlate.state': value});
    };

    return (
        <ControlGroup labelWidth={200}>
            <Control>
                <Select
                    label="License Plate State"
                    onChange={handleLicenseStateChange}>
                    <option>Select a State</option>
                    {Object.keys(weighted).map((state, i) => {
                        return (
                            <option
                                key={i}
                                selected={state === licenseState}
                                value={state}>
                                {abbreviations[state]}
                            </option>
                        );
                    })}
                </Select>
            </Control>
            {blacklisted && (
                <p className={classes.blacklistedMsg}>
                    Sorry!  We cannot lookup license plates in this state.
                    Please try a different lookup method.
                </p>
            )}
            <Control>
                <TextInput
                    key="licensePlate"
                    label="License Plate Number"
                    placeholder="ECTO 1"
                    value={licensePlate}
                    onChange={handleLicensePlateChange} />
            </Control>
            {loading ? (
                <Loader
                    type="ThreeDots"
                    color={primaryColor}
                    height="100"
                    width="100" />
            ) : (
                <Button
                    className={classes.findVehicleButton}
                    disabled={disableLicenseButton}
                    btnStyle="add"
                    onClick={handleFindVehicleClick}>
                    Find Vehicle
                </Button>
            )}
            {failure && (
                <Fragment>
                    <p className="info">
                        {failureCount > 2 ? (
                            <span>
                                We were not able to identify your vehicle using the
                                license plate/state that you provided. Please try one of
                                the alternative methods below or contact a representative
                                at {contactPhone}.
                            </span>
                        ) : (
                            <span>
                                We experienced a problem while trying to find your
                                vehicle. Please try an alternative method or contact
                                a representative at {contactPhone}.
                            </span>
                        )}
                    </p>
                    <ul className={classes.lookupOptions}>
                        <li>
                            <a onClick={() => handleTypeChange('vin')}>
                                Enter your VIN
                            </a>
                        </li>
                        <li>
                            <a onClick={() => handleTypeChange('neither')}>
                                Enter your vehicle&apos;s year, make and model
                            </a>
                        </li>
                    </ul>
                </Fragment>
            )}
        </ControlGroup>
    );
};

License.propTypes = {
    handleChange: PropTypes.func.isRequired,
    handleTypeChange: PropTypes.func.isRequired
};

export default injectSheet(styles)(License);